import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, MapPin, Users, Share2, Info, Heart, CheckCircle, PlayCircle, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useBackNavigation } from "@/hooks/useBackNavigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { isEventLikesEnabled } from "@/utils/featureFlags";
import { useAuth } from "@/contexts/AuthContext";
import { addAlert } from "@/data/alertsStore";
import { useEventMetricsStore } from "@/data/eventMetricsStore";
import { usePartiesStore, getValidatedPartyDisplayData } from "@/data/partiesStore";
import { useEventsStore } from "@/data/eventsStore";
import { getPresentationV2ForEntity } from "@/data/presentationsStore";

type TranslatedEvent = {
  id: string;
  partyAcronym: string;
  title: Record<string, string>;
  description: Record<string, string>;
  date: string;
  location: Record<string, string>;
  type: "conference" | "meeting" | "formation" | "campagne" | "autre";
  status: "upcoming" | "ongoing" | "past";
  registrationType?: "public" | "mandatory";
  registrationLink?: string;
  externalLink?: string;
  organizerLogo?: string;
};

const eventsData: TranslatedEvent[] = [
  // ── 8 Événements Partis ──────────────────────────────────────────────────────
  {
    id: "p1", partyAcronym: "RNI",
    title: { fr: "Conférence nationale sur l'investissement", ar: "المؤتمر الوطني حول الاستثمار", zgh: "ⴰⴳⵔⴰⵡ ⴰⵏⴰⵎⵓⵔ ⵅⴼ ⵓⵙⵙⴰⵔⵓ" },
    description: { fr: "Rencontre avec les acteurs économiques pour discuter des opportunités d'investissement régional et des perspectives du programme RNI 2026." },
    date: "2026-05-15", location: { fr: "Rabat — Palais des Congrès", ar: "الرباط — قصر المؤتمرات", zgh: "ⵕⵕⴱⴰⵟ" },
    type: "conference", status: "upcoming", registrationType: "public", externalLink: "https://rni.ma/"
  },
  {
    id: "p2", partyAcronym: "PAM",
    title: { fr: "Caravane de proximité — Souss-Massa", ar: "قافلة القرب - سوس ماسة", zgh: "ⵜⴰⴳⵔⴰⵡⵜ ⵏ ⵜⵖⵔⵎⵜ" },
    description: { fr: "Rencontres directes avec les citoyens pour écouter leurs préoccupations dans les communes rurales de la région Souss-Massa." },
    date: "2026-06-20", location: { fr: "Agadir — Place Al Amal", ar: "أكادير", zgh: "ⴰⴳⴰⴷⵉⵔ" },
    type: "campagne", status: "upcoming", registrationType: "public"
  },
  {
    id: "p3", partyAcronym: "PI",
    title: { fr: "Formation des jeunes cadres Istiqlal", ar: "تكوين الأطر الشابة الاستقلالية", zgh: "ⴰⵙⵎⵖⵔ ⵏ ⵉⵄⵔⵔⵉⵎⵏ" },
    description: { fr: "Programme de formation intensive pour les jeunes militants sur la gouvernance locale et la communication politique électorale." },
    date: "2026-07-10", location: { fr: "Casablanca — Siège National PI", ar: "الدار البيضاء", zgh: "ⴰⵏⴼⴰ" },
    type: "formation", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "p4", partyAcronym: "USFP",
    title: { fr: "Séminaire : Protection sociale et emploi", ar: "ندوة حول الحماية الاجتماعية", zgh: "ⴰⵎⴰⵍⴰⵙ ⵅⴼ ⵉⵣⵔⴼⴰⵏ ⵉⵏⴰⵎⵓⵏⵏ" },
    description: { fr: "L'USFP organise un séminaire national sur les réformes sociales, l'emploi des jeunes et les propositions du parti pour les législatives 2026." },
    date: "2026-06-05", location: { fr: "Rabat — Hôtel Sofitel", ar: "الرباط", zgh: "ⵕⵕⴱⴰⵟ" },
    type: "conference", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "p5", partyAcronym: "PPS",
    title: { fr: "Congrès régional PPS — Marrakech", ar: "مؤتمر جهوي لحزب التقدم والاشتراكية", zgh: "ⴰⴳⵔⴰⵡ ⴰⵖⵍⵏⴰⵡ ⵏ PPS" },
    description: { fr: "Congrès des militants du PPS pour débattre du programme électoral régional et définir les priorités pour les législatives 2026." },
    date: "2026-06-18", location: { fr: "Marrakech — Palais des Congrès", ar: "مراكش", zgh: "ⵎⵕⵕⴰⴽⵛ" },
    type: "meeting", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "p6", partyAcronym: "MP",
    title: { fr: "Forum rural : Agriculture & Développement", ar: "منتدى العالم القروي والتنمية", zgh: "ⴰⵙⵎⵓⵏ ⵏ ⵓⵣⵣⵓⵍⵍⵓ" },
    description: { fr: "Le Mouvement Populaire réunit élus locaux, agriculteurs et experts pour débattre des défis du monde rural dans la région Fès-Meknès." },
    date: "2026-07-10", location: { fr: "Meknès — Chambre d'Agriculture", ar: "مكناس", zgh: "ⵎⴽⵏⴰⵙ" },
    type: "conference", status: "upcoming", registrationType: "public"
  },
  {
    id: "p7", partyAcronym: "UC",
    title: { fr: "Assemblée régionale UC — Agadir", ar: "الجمع الجهوي للاتحاد الدستوري", zgh: "ⴰⵎⵓⵙⵙⵓ ⴰⵖⵍⵏⴰⵡ ⵏ UC" },
    description: { fr: "Réunion des cadres de l'Union Constitutionnelle pour valider les listes électorales de la région Souss-Massa pour les législatives 2026." },
    date: "2026-07-25", location: { fr: "Agadir — Hôtel Royal Atlas", ar: "أكادير", zgh: "ⴰⴳⴰⴷⵉⵔ" },
    type: "meeting", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "p8", partyAcronym: "PAM",
    title: { fr: "Caravane électorale — Béni Mellal", ar: "قافلة انتخابية - بني ملال", zgh: "ⵜⴰⴳⵔⴰⵡⵜ ⵏ ⵓⵙⵜⵓⵏ" },
    description: { fr: "Caravane de proximité du PAM dans les communes rurales de Béni Mellal-Khénifra pour présenter le programme électoral aux citoyens." },
    date: "2026-08-15", location: { fr: "Béni Mellal — Place Centrale", ar: "بني ملال", zgh: "ⴱⵏⵉ ⵎⵍⵍⴰⵍ" },
    type: "campagne", status: "upcoming", registrationType: "public"
  },

  // ── 8 Événements Citoyens & Institutions ─────────────────────────────────────
  {
    id: "c1", partyAcronym: "Centre Marocain du Volontariat",
    title: { fr: "Forum Africain des Organisations de la Société Civile", ar: "المنتدى الأفريقي لمنظمات المجتمع المدني", zgh: "ⴰⵙⵎⵓⵏ ⴰⴼⵕⵉⵇⵉ ⵏ ⵜⵎⵙⵎⵓⵏⵉⵏ" },
    description: { fr: "Premier forum continental sur le volontariat africain — 'Le volontariat au service de l'intégration et de la solidarité des peuples africains'." },
    date: "2026-04-21", location: { fr: "Rabat — Palais des Congrès", ar: "الرباط", zgh: "ⵕⵕⴱⴰⵟ" },
    type: "conference", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "c2", partyAcronym: "Délégation UE au Maroc",
    title: { fr: "Forum National du Dialogue Multi-Acteurs", ar: "المنتدى الوطني للحوار متعدد الأطراف", zgh: "ⴰⵙⵎⵓⵏ ⴰⵏⴰⵎⵓⵔ ⵏ ⵓⵎⵢⴰⵡⴰⵙ" },
    description: { fr: "Forum PASSC réunissant institutions publiques, ONG et collectivités pour renforcer le rôle de la société civile dans la gouvernance publique." },
    date: "2026-05-20", location: { fr: "Rabat — Siège Délégation UE", ar: "الرباط", zgh: "ⵕⵕⴱⴰⵟ" },
    type: "conference", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "c3", partyAcronym: "ANAPEC",
    title: { fr: "Forum Future of Work Africa (FOWFA) 2026", ar: "منتدى مستقبل العمل في أفريقيا", zgh: "ⴰⵙⵎⵓⵏ ⵏ ⵓⵙⴽⴽⵉⵍ ⴳ ⵜⴼⵔⵉⵇⵉⵜ" },
    description: { fr: "3e édition dédiée aux mutations du travail à l'ère numérique, réunissant décideurs publics, experts et acteurs de la société civile africaine." },
    date: "2026-05-21", location: { fr: "Casablanca — Casa Anfa", ar: "الدار البيضاء", zgh: "ⴰⵏⴼⴰ" },
    type: "conference", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "c4", partyAcronym: "Transparency Maroc",
    title: { fr: "Atelier : Observation électorale citoyenne 2026", ar: "ورشة: مراقبة الانتخابات المواطنة", zgh: "ⴰⵎⴰⵍⴰⵙ: ⵜⴰⵔⵔⴰ ⵏ ⵓⵙⵜⵓⵏ" },
    description: { fr: "Formation de bénévoles pour surveiller le bon déroulement des élections législatives du 23 septembre 2026 dans toutes les régions du Maroc." },
    date: "2026-06-12", location: { fr: "Casablanca — Siège Transparency Maroc", ar: "الدار البيضاء", zgh: "ⴰⵏⴼⴰ" },
    type: "formation", status: "upcoming", registrationType: "public"
  },
  {
    id: "c5", partyAcronym: "CESE",
    title: { fr: "Consultation citoyenne : Priorités nationales 2026-2030", ar: "التشاور المواطن حول الأولويات الوطنية", zgh: "ⴰⵎⵢⴰⵡⴰⵙ ⴰⵏⴰⵎⵓⵔ ⵅⴼ ⵉⵙⵙⴰⵜⵉⵜⵏ" },
    description: { fr: "Le CESE lance des consultations régionales pour identifier les priorités des Marocains à l'approche des élections législatives de septembre 2026." },
    date: "2026-06-20", location: { fr: "Rabat — Siège CESE", ar: "الرباط", zgh: "ⵕⵕⴱⴰⵟ" },
    type: "meeting", status: "upcoming", registrationType: "mandatory"
  },
  {
    id: "c6", partyAcronym: "Initiative Citoyenne Jeunes",
    title: { fr: "Marché civique : Connais tes droits électoraux", ar: "السوق المدني: اعرف حقوقك الانتخابية", zgh: "ⴰⵙⵙⴰⵔⵓ ⴰⴳⴷⵓⴷ: ⵙⵙⵉⵏ ⵉⵣⵔⴼⴰⵏ ⵏⵏⴽ" },
    description: { fr: "Initiative de jeunes bénévoles pour sensibiliser les primo-votants à leurs droits électoraux et au processus de vote avant les législatives 2026." },
    date: "2026-08-30", location: { fr: "Salé — Place Bab Mrisa", ar: "سلا", zgh: "ⵙⵍⴰ" },
    type: "campagne", status: "upcoming", registrationType: "public"
  },
  {
    id: "c7", partyAcronym: "FLSH Casablanca",
    title: { fr: "Colloque : Transformations sociales à l'ère de l'IA", ar: "ندوة: التحولات الاجتماعية في عصر الذكاء الاصطناعي", zgh: "ⴰⴳⵔⴰⵡ: ⵜⵉⵙⵏⵜⵉⵍⵉⵏ ⵉⵏⴰⵎⵓⵏⵏ" },
    description: { fr: "Colloque scientifique international de la FLSH Aïn Chock sur les impacts sociaux et humains de la technologie et de l'intelligence artificielle." },
    date: "2026-05-28", location: { fr: "Casablanca — FLSH Aïn Chock", ar: "الدار البيضاء", zgh: "ⴰⵏⴼⴰ" },
    type: "conference", status: "upcoming", registrationType: "public"
  },
  {
    id: "c8", partyAcronym: "Association Citoyens & Territoires",
    title: { fr: "Nettoyage écologique — Plage de Tanger", ar: "حملة بيئية — شاطئ طنجة", zgh: "ⴰⵙⴼⴹ ⴰⵎⵓⵙⵙⵓ — ⵟⴰⵏⵊⴰ" },
    description: { fr: "Grande opération citoyenne de nettoyage de la plage de Tanger et atelier de sensibilisation à la protection de l'environnement côtier marocain." },
    date: "2026-05-05", location: { fr: "Tanger — Plage Malabata", ar: "طنجة", zgh: "ⵟⴰⵏⵊⴰ" },
    type: "autre", status: "upcoming", registrationType: "public"
  },
];

const typeLabels: Record<string, Record<string, string>> = {
  fr: { conference: "Conférence", meeting: "Réunion", formation: "Formation", campagne: "Campagne", autre: "Autre" },
  ar: { conference: "مؤتمر", meeting: "اجتماع", formation: "تكوين", campagne: "حملة", autre: "أخرى" },
  zgh: { conference: "ⴰⴳⵔⴰⵡ", meeting: "ⴰⵎⵙⴰⵡⴰⵍ", formation: "ⴰⵙⵎⵖⵔ", campagne: "ⵜⴰⵎⵀⵍⴰ", autre: "ⵢⴰⴹⵏ" },
};

const statusLabels: Record<string, Record<string, string>> = {
  fr: { upcoming: "À venir", ongoing: "En cours", past: "Terminé" },
  ar: { upcoming: "قادم", ongoing: "جاري", past: "منتهي" },
  zgh: { upcoming: "ⴰⴷ ⵉⴷⴷⵓ", ongoing: "ⵉⵜⵜⴻⴷⴷⵓ", past: "ⵉⴼⵓⴽⴽ" },
};

const pageLabels: Record<string, Record<string, string>> = {
  fr: { back: "Retour à l'agenda", details: "Détails de l'événement", organizer: "Organisateur", date: "Date", location: "Lieu", type: "Type", status: "Statut", share: "Partager", allEvents: "Voir tous les événements", access: "Accès", public: "Ouvert au public", mandatory: "Enregistrement obligatoire", registerBtn: "S'enregistrer", regName: "Nom complet", regEmail: "Email", regPhone: "Téléphone", regSubmit: "Envoyer la demande", regSuccess: "Votre demande a été envoyée à l'organisateur pour prise en charge. La plateforme n'est qu'un intermédiaire, la validation finale relève de l'organisateur.", required: "Requis" },
  ar: { back: "العودة للجدول", details: "تفاصيل الفعالية", organizer: "المنظم", date: "التاريخ", location: "المكان", type: "النوع", status: "الحالة", share: "مشاركة", allEvents: "جميع الفعاليات", access: "الولوج", public: "مفتوح للعموم", mandatory: "التسجيل الزامي", registerBtn: "تسجيل", regName: "الاسم الكامل", regEmail: "البريد الإلكتروني", regPhone: "الهاتف", regSubmit: "إرسال الطلب", regSuccess: "تم إرسال طلبكم إلى المنظم للمعالجة. المنصة هي مجرد وسيط، والتحقق النهائي من قبل المنظم.", required: "إلزامي" },
  zgh: { back: "ⵓⵖⴰⵍ ⵖⵔ ⵓⵖⴰⵡⴰⵙ", details: "ⵉⵙⴼⴽⴰ ⵏ ⵓⵎⵓⵙⵙⵓ", organizer: "ⴰⵎⵙⵙⵓⴷⵙ", date: "ⴰⵙⴰⴽⵓⴷ", location: "ⴰⴷⵖⴰⵔ", type: "ⴰⵏⴰⵡ", status: "ⴰⴷⴷⴰⴷ", share: "ⴱⴹⵓ", allEvents: "ⵉⵎⵓⵙⵙⵓⵜⵏ ⵎⴰⵕⵕⴰ", access: "ⴰⴽⵛⵛⵓⵎ", public: "ⵉⵕⵥⵎ ⵉ ⵓⴳⴷⵓⴷ", mandatory: "ⴰⵣⵎⵎⴻⵎ ⵉⵅⵚⵚⴰ", registerBtn: "ⵣⵎⵎⴻⵎ", regName: "ⵉⵙⵎ", regEmail: "ⵉⵎⴰⵢⵍ", regPhone: "ⵜⵉⵍⵉⴼⵓⵏ", regSubmit: "ⴰⵣⵏ", regSuccess: "ⵜⵜⵓⵢⴰⵣⴰⵏ ⵜⵜⵔ ⵏⵏⴽ ⵉ ⵓⵎⵙⵏⵎⴰⵍⴰ. ⴰⵙⴰⵢⵔⴰⵔ ⵉⴳⴰ ⵖⴰⵙ ⴰⵎⵙⴰⵡⴰⴹ, ⴰⵙⵉⵖⵣⴼ ⵉⴳⴰ ⵙⴳ ⵓⵎⵙⵏⵎⴰⵍⴰ.", required: "ⵉⵅⵚⵚⴰ" },
};

const EvenementDetailPage = () => {
  const { lang, isRtl } = useI18n();
  const { id } = useParams<{ id: string }>();
  const profiles = usePartiesStore(state => state.profiles);
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const labels = pageLabels[lang] || pageLabels.fr;
  const handleBack = useBackNavigation("/agenda-national");

  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationSubmitted, setRegistrationSubmitted] = useState(false);
  const { userLikes, toggleLike } = useEventMetricsStore();
  const [hasReminder, setHasReminder] = useState(false);
  const [eventLikesOn, setEventLikesOn] = useState(isEventLikesEnabled());
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    useEventMetricsStore.getState().fetchMetrics();
    useEventsStore.getState().fetchEvents(true); // Ensure events are loaded
    const handleLikesChange = (e: any) => setEventLikesOn(e.detail?.enabled ?? true);
    window.addEventListener("eventLikesChanged", handleLikesChange);
    return () => window.removeEventListener("eventLikesChanged", handleLikesChange);
  }, []);

  const getEventData = () => {
    const baseEvent = eventsData.find(e => e.id === id);
    if (baseEvent) return baseEvent;
    
    // Check Zustand store instead of localStorage
    const events = useEventsStore.getState().events;
    const localEvent = events.find((e) => e.id === id && e.status === "approved");
    if (localEvent) {
      return {
        ...localEvent,
        partyAcronym: localEvent.partyAcronym || "Org",
        title: typeof localEvent.title === 'string' ? { fr: localEvent.title, ar: localEvent.title, zgh: localEvent.title } : localEvent.title,
        description: typeof localEvent.description === 'string' ? { fr: localEvent.description, ar: localEvent.description, zgh: localEvent.description } : localEvent.description,
        date: localEvent.date || new Date().toISOString().split('T')[0],
        location: typeof localEvent.location === 'string' ? { fr: localEvent.location, ar: localEvent.location, zgh: localEvent.location } : (localEvent.location || {fr: "Non spécifié"}),
        type: localEvent.type || "autre",
        status: "upcoming",
        registrationType: localEvent.registrationType || "public",
        registrationLink: (localEvent as any).registrationLink,
        externalLink: localEvent.externalLink,
        organizerLogo: localEvent.organizerLogo || (localEvent.organizerStatus === 'ong' || localEvent.organizerStatus === 'institution' ? getPresentationV2ForEntity(localEvent.partyAcronym, localEvent.organizerStatus.toUpperCase())?.logoUrl : undefined)
      } as TranslatedEvent;
    }
    return undefined;
  };

  const eventData = getEventData();
  if (!eventData) return <Navigate to="/" replace />;
  const isLiked = !!userLikes[eventData.id];

  const handleRegisterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const name = (form.elements.namedItem('regName') as HTMLInputElement).value;
    const email = (form.elements.namedItem('regEmail') as HTMLInputElement).value;
    const phone = (form.elements.namedItem('regPhone') as HTMLInputElement).value;
    
    const newReg = {
      id: Date.now().toString(),
      eventId: event.id,
      eventTitle: event.title,
      organizer: event.partyAcronym,
      name, email, phone,
      submittedAt: new Date().toISOString()
    };
    
    const existing = JSON.parse(localStorage.getItem('eventRegistrations') || '[]');
    localStorage.setItem('eventRegistrations', JSON.stringify([...existing, newReg]));
    
    // Activity log
    const activityLogs = JSON.parse(localStorage.getItem('activityLogs') || '[]');
    activityLogs.push({
      id: Date.now().toString(),
      section: 'Événements',
      category: 'Inscription',
      action: `Inscription à l'événement: ${event.title}`,
      user: name,
      details: `Email: ${email}, Tél: ${phone}`,
      date: new Date().toISOString()
    });
    localStorage.setItem('activityLogs', JSON.stringify(activityLogs));

    setRegistrationSubmitted(true);
    setIsRegistering(false);
  };

  const event = {
    ...eventData,
    title: eventData.title[lang] || eventData.title.fr,
    description: eventData.description[lang] || eventData.description.fr,
    location: eventData.location[lang] || eventData.location.fr,
    externalLink: eventData.externalLink,
    organizerLogo: eventData.organizerLogo,
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Lien copié dans le presse-papier !");
  };

  const handleReminder = () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour activer le rappel.");
      return;
    }
    
    addAlert({
      title: { fr: `Rappel : ${event.title}`, ar: `تذكير : ${event.title}`, zgh: `ⴰⵙⵎⴽⵜⵉ : ${event.title}` },
      description: { 
        fr: `L'événement organisé par ${event.partyAcronym} aura lieu le ${event.date} à ${event.location}.`, 
        ar: `الحدث المنظم من طرف ${event.partyAcronym} سيقام يوم ${event.date} في ${event.location}.`, 
        zgh: `ⴰⵎⵓⵙⵙⵓ ⵏ ${event.partyAcronym} ⴰⴷ ⵉⵜⵜⵓⵙⴽⴰⵔ ⴰⵙⵙ ⵏ ${event.date} ⴳ ${event.location}.` 
      },
      sourceName: "Kayn'in Agenda",
      sourceType: "Institution",
      region: "Toutes",
      theme: "Politique",
      isImportant: true,
      targetUserId: user.id,
      notificationType: "reminder_event",
      linkUrl: window.location.href
    });
    
    setHasReminder(true);
    toast.success("Rappel activé ! Vous recevrez une notification.");
  };

  // Determine Image URL
  const imageUrl = `/images/events/${event.id}.jpg`;
  const fallbackImageUrl = (event as any).imageUrl || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 container py-24 max-w-5xl">
        <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
          <Link to="/agenda-national">
            <BackArrow className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
            {labels.back}
          </Link>
        </Button>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5 }}
          className="bg-card rounded-3xl border border-border overflow-hidden shadow-elevated flex flex-col"
        >
          <div className="flex flex-col md:flex-row min-h-[450px]">
            {/* Left side: Image and overlays */}
            <div className="w-full md:w-1/2 min-h-[350px] p-8 flex flex-col justify-end relative overflow-hidden group shrink-0 bg-slate-950 border-b md:border-b-0 md:border-r border-border/50">
              <img 
                src={imageUrl} 
                onError={(e) => { 
                   if (!e.currentTarget.src.includes('unsplash.com')) {
                       e.currentTarget.src = fallbackImageUrl;
                   } 
                }} 
                alt={event.title} 
                className="absolute inset-0 w-full h-full object-contain bg-white transition-transform duration-700 ease-in-out group-hover:scale-105" 
              />
              {/* Grandient d'assombrissement pour la lisibilité */}
              <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/80 via-black/30 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/90 via-black/60 to-transparent pointer-events-none" />
              
              <div className="absolute top-6 left-6 right-6 flex flex-wrap gap-2 items-start z-10">
                 <Badge variant="secondary" className="bg-white/90 backdrop-blur-md text-black font-body font-bold text-xs uppercase tracking-wide px-3 py-1 shadow-sm">
                   {typeLabels[lang]?.[event.type] || event.type}
                 </Badge>

                 <Badge className={`${event.status === 'upcoming' ? 'bg-green-600' : 'bg-primary'} text-white shadow-md font-bold px-3 py-1 border-0`}>
                   {statusLabels[lang]?.[event.status] || event.status}
                 </Badge>

                 {event.registrationType && (
                  <Badge variant={event.registrationType === 'mandatory' ? "destructive" : "secondary"} className="bg-black/50 backdrop-blur-md text-white border border-white/20 font-bold px-3 py-1">
                    {event.registrationType === 'mandatory' ? labels.mandatory : labels.public}
                  </Badge>
                 )}
              </div>
              
              <div className="relative z-10 mt-auto">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-display font-black text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] leading-tight mb-6">
                  {event.title}
                </h1>

                {/* Organizer Info overlay on image */}
                <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md p-3 rounded-2xl border border-white/20 inline-flex">
                  <div className="relative w-12 h-12 rounded-full bg-white border border-white/30 overflow-hidden flex items-center justify-center shrink-0 shadow-lg">
                     <img 
                        src={event.organizerLogo || getValidatedPartyDisplayData(event.partyAcronym, event.partyAcronym, '').logoUrl || `/images/logos/${event.partyAcronym.toLowerCase()}.png`} 
                        alt={event.partyAcronym}
                        className="w-full h-full object-contain p-1.5"
                        onError={(e) => {
                           e.currentTarget.style.display = 'none';
                           if (e.currentTarget.parentElement && !e.currentTarget.parentElement.querySelector('svg')) {
                               e.currentTarget.parentElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary/70"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>`;
                           }
                        }}
                     />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-white/70 font-body uppercase tracking-wider">{labels.organizer}</span>
                    <span className="text-base font-display font-bold text-white drop-shadow-sm">{event.partyAcronym}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side: Description and Details */}
            <div className="w-full md:w-1/2 p-8 md:p-10 flex flex-col bg-card/50">
              <div className="flex justify-between items-start gap-4 border-b border-border/50 pb-6 mb-6">
                 <div className="flex flex-col gap-3">
                   <div className="flex items-center text-foreground font-body font-medium text-sm md:text-base bg-muted/50 px-3 py-1.5 rounded-lg border border-border">
                     <Calendar className="w-4 h-4 mr-3 text-primary" />
                     <span>{event.date}</span>
                   </div>
                   <div className="flex items-center text-foreground font-body font-medium text-sm md:text-base bg-muted/50 px-3 py-1.5 rounded-lg border border-border">
                     <MapPin className="w-4 h-4 mr-3 text-primary" />
                     <span>{event.location}</span>
                   </div>
                 </div>

                 {eventLikesOn && (
                   <div className="flex gap-2 shrink-0">
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={handleShare}
                        title="Partager"
                        className="w-10 h-10 rounded-full border-2 border-border/60 shadow-sm transition-all duration-300 hover:scale-105 hover:border-blue-200 hover:bg-blue-50/50 bg-background"
                      >
                        <Share2 className="w-4 h-4 text-muted-foreground hover:text-blue-500 transition-colors" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => {
                            const newState = !isLiked;
                            toggleLike(event.id, 'event');
                            if (newState) {
                              toast("Ajouté aux favoris", { icon: "❤️" });
                              if (user && user.role === 'citizen') {
                                 addAlert({
                                   title: { fr: `Événement favori : ${event.title}`, ar: `حدث مفضل : ${event.title}`, zgh: `ⴰⵎⵓⵙⵙⵓ ⵉⵙⵎⵏⴰⴷⵏ : ${event.title}` },
                                   description: { 
                                     fr: `Rappel : L'événement "${event.title}" aura lieu le ${event.date} à ${event.location}.`, 
                                     ar: `تذكير : الحدث "${event.title}" سيقام يوم ${event.date} في ${event.location}.`, 
                                     zgh: `ⴰⵙⵎⴽⵜⵉ : ⴰⵎⵓⵙⵙⵓ "${event.title}" ⴰⴷ ⵉⵜⵜⵓⵙⴽⴰⵔ ⴰⵙⵙ ⵏ ${event.date} ⴳ ${event.location}.` 
                                   },
                                   sourceName: event.partyAcronym,
                                   sourceType: "Parti Politique",
                                   region: "Toutes",
                                   theme: "Politique",
                                   isImportant: false,
                                   targetUserId: user.id,
                                   notificationType: "reminder_event",
                                   linkUrl: window.location.href
                                 });
                              }
                            } else {
                              toast("Retiré des favoris", { icon: "💔" });
                            }
                        }} 
                        className={`w-10 h-10 rounded-full border-2 shadow-sm transition-all duration-300 hover:scale-105 bg-background ${
                          isLiked ? "border-pink-500 bg-pink-50 hover:bg-pink-100" : "border-border/60 hover:border-pink-200 hover:bg-pink-50/50"
                        }`}
                        title="Ajouter aux favoris"
                      >
                        <Heart className={`w-5 h-5 transition-colors duration-300 ${isLiked ? "fill-pink-500 text-pink-500" : "text-muted-foreground group-hover:text-pink-400"}`} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={handleReminder}
                        title="Me rappeler"
                        className={`w-10 h-10 rounded-full border-2 shadow-sm transition-all duration-300 hover:scale-105 bg-background ${
                          hasReminder ? "border-amber-500 bg-amber-50 hover:bg-amber-100" : "border-border/60 hover:border-amber-200 hover:bg-amber-50/50"
                        }`}
                      >
                        <Bell className={`w-5 h-5 transition-colors duration-300 ${hasReminder ? "fill-amber-500 text-amber-500" : "text-muted-foreground hover:text-amber-400"}`} />
                      </Button>
                   </div>
                 )}
              </div>

              <div className="space-y-6 flex-1">
                 <div>
                   <h3 className="text-xs font-body font-bold text-muted-foreground uppercase tracking-wider mb-3">{labels.details}</h3>
                   <div className="bg-muted/20 p-4 rounded-xl border border-border/50">
                     <p className={`text-base font-body leading-relaxed text-foreground transition-all duration-300 ${!isDescriptionExpanded ? 'line-clamp-4' : ''}`}>
                       {event.description}
                     </p>
                     {event.description && event.description.length > 200 && (
                       <button 
                         onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                         className="text-primary font-body font-semibold text-sm mt-2 hover:underline focus:outline-none"
                       >
                         {isDescriptionExpanded ? (lang === 'ar' ? 'عرض أقل' : lang === 'zgh' ? 'ⵙⴽⵏ ⴷⵔⵓⵙ' : 'Voir moins') : (lang === 'ar' ? 'قراءة المزيد' : lang === 'zgh' ? 'ⵖⵔ ⵓⴳⴳⴰⵔ' : 'Lire la suite')}
                       </button>
                     )}
                     
                     {event.externalLink && (
                       <div className="mt-6 pt-4 border-t border-border/40">
                         <Button asChild variant="secondary" className="w-full sm:w-auto font-body bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-300 transition-colors shadow-sm">
                           <a href={event.externalLink} target="_blank" rel="noopener noreferrer">
                            {lang === 'ar' ? 'للمزيد من المعلومات' : lang === 'zgh' ? 'ⵙⵙⵏ ⵓⴳⴳⴰⵔ' : 'En savoir plus'} <ArrowRight className="w-4 h-4 ml-2" />
                           </a>
                         </Button>
                       </div>
                     )}
                   </div>
                 </div>

                 <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/30 border border-border/50">
                    <Info className="w-5 h-5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground font-body mb-0.5">{labels.access}</p>
                      <p className="text-sm font-body font-bold text-foreground">
                        {event.registrationType === 'mandatory' ? labels.mandatory : labels.public}
                      </p>
                    </div>
                 </div>
              </div>
            </div>
          </div>

          {/* Bottom Action Area (Registration) */}
          {(event.registrationType === 'mandatory' || isRegistering || registrationSubmitted) && (
            <div className="p-8 md:p-10 bg-muted/10 border-t border-border flex justify-center">
               <div className="w-full max-w-xl">
                 {event.registrationType === 'mandatory' && !isRegistering && !registrationSubmitted && (
                   <Button 
                     onClick={() => {
                       const link = (event as any).registrationLink;
                       if (link) {
                         if (link.includes('@') && !link.startsWith('http')) {
                           window.location.href = `mailto:${link}`;
                         } else {
                           window.open(link.startsWith('http') ? link : `https://${link}`, '_blank');
                         }
                       } else {
                         setIsRegistering(true);
                       }
                     }} 
                     size="lg" 
                     className="w-full font-display font-bold text-lg py-6 shadow-md transition-transform hover:scale-[1.02]"
                   >
                     <CheckCircle className="w-5 h-5 mr-2" /> {labels.registerBtn}
                   </Button>
                 )}

                 {isRegistering && (
                   <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                     <form onSubmit={handleRegisterSubmit} className="bg-background p-6 rounded-2xl border border-border shadow-sm space-y-4">
                       <h3 className="font-display font-bold text-xl mb-4">{labels.registerBtn}</h3>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                           <Label className="font-body text-muted-foreground">{labels.regName} *</Label>
                           <Input name="regName" required className="mt-1.5" />
                         </div>
                         <div>
                           <Label className="font-body text-muted-foreground">{labels.regEmail} *</Label>
                           <Input name="regEmail" type="email" required className="mt-1.5" />
                         </div>
                       </div>
                       <div>
                         <Label className="font-body text-muted-foreground">{labels.regPhone}</Label>
                         <Input name="regPhone" type="tel" pattern="^[+]?[0-9]{10,}$" title="num incorrect" className="mt-1.5" />
                       </div>
                       <div className="pt-4 flex gap-3">
                         <Button type="submit" size="lg" className="font-body font-bold flex-1 shadow-md">{labels.regSubmit}</Button>
                         <Button type="button" size="lg" variant="outline" onClick={() => setIsRegistering(false)} className="font-body">Annuler</Button>
                       </div>
                     </form>
                   </motion.div>
                 )}

                 {registrationSubmitted && (
                   <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 p-8 rounded-2xl text-center shadow-sm">
                     <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                     <h3 className="font-display font-bold text-2xl text-foreground mb-2">Demande envoyée</h3>
                     <p className="text-base font-body text-muted-foreground">{labels.regSuccess}</p>
                   </motion.div>
                 )}
               </div>
            </div>
          )}
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default EvenementDetailPage;
